import { useState } from 'react'
import { ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { useRouteContext, useRouter } from '@tanstack/react-router'
import { Alert, App, Button, Empty } from 'antd'
import type { ContractDocumentBatchSetStatusInput, ContractDocumentStatus } from '@super-mega/server/contracts/contract'
import { usePageTableNavigation } from '@/hooks/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { contractDocumentColumns, type ContractDocumentRow } from './contract-document-columns.tsx'
import { ContractDocumentEditorModal } from './contract-document-editor-modal.tsx'
import type { ContractDocumentSearch } from './contract-document-search.ts'
import { ContractDocumentToolbar } from './contract-document-toolbar.tsx'
import { ContractStatusModal } from './contract-status-modal.tsx'
import { useContractDocumentInvalidation } from './hooks/use-contract-document-invalidation.ts'

type StatusEditing = {
  target: ContractDocumentBatchSetStatusInput['target'] | 'allOrder'
  label: string
  initialStatus?: ContractDocumentStatus
  defaultStatus?: ContractDocumentStatus
}
type Filters = Pick<ContractDocumentSearch, 'keyword' | 'type' | 'status'>

export type ContractDocumentSourceOrder = NonNullable<ContractDocumentRow['sourceOrder']>

type ContractDocumentsTableProps = {
  search: ContractDocumentSearch
  onSearchChange: (search: ContractDocumentSearch) => void
} & (
  | { order: ContractDocumentSourceOrder; onOpenSourceOrder?: never }
  | { order?: never; onOpenSourceOrder: (source: ContractDocumentSourceOrder) => void }
)

export function ContractDocumentsTable({
  search,
  onSearchChange,
  order,
  onOpenSourceOrder
}: ContractDocumentsTableProps) {
  const trpc = useTRPC()
  const router = useRouter()
  const { currentSession } = useRouteContext({ from: '/_authenticated' })
  const { message } = App.useApp()
  const invalidate = useContractDocumentInvalidation()
  const [editing, setEditing] = useState<'new'>()
  const [statusEditing, setStatusEditing] = useState<StatusEditing>()
  const [selection, setSelection] = useState<{ search: ContractDocumentSearch; ids: number[] }>({ search, ids: [] })
  const permissions = currentSession.permissions
  const canManageContent = currentSession.isSuperAdmin || permissions.includes('contract.document.update')
  const canViewContent = currentSession.isSuperAdmin || permissions.includes('contract.document.read')
  const canManage =
    currentSession.isSuperAdmin ||
    permissions.some((permission) =>
      ['contract.document.confirm', 'contract.document.activate', 'contract.document.void'].includes(permission)
    )
  const canReadOrders =
    currentSession.isSuperAdmin ||
    permissions.some((permission) => permission === 'order.rental.read' || permission === 'order.service.read')
  const documents = useQuery({
    ...trpc.contract.document.list.queryOptions(search),
    enabled: !order,
    placeholderData: keepPreviousData
  })
  const orderDocuments = useQuery({
    ...trpc.order.contracts.list.queryOptions({ orderType: order?.type ?? 'rental', orderId: order?.id ?? 0, search }),
    enabled: Boolean(order),
    placeholderData: keepPreviousData
  })
  const query = order ? orderDocuments : documents
  const mutation = useMutation(trpc.contract.document.batchSetStatus.mutationOptions())
  const orderMutation = useMutation(trpc.order.contracts.batchSetStatus.mutationOptions())
  const pending = mutation.isPending || orderMutation.isPending
  const pageIds = new Set(query.data?.data.map((row) => row.id))
  const selectedIds = selection.search === search ? selection.ids.filter((id) => pageIds.has(id)) : []
  const table = usePageTableNavigation<ContractDocumentSearch, Filters>({
    search,
    navigate: ({ search: next }) => {
      onSearchChange(typeof next === 'function' ? next(search) : next)
    },
    defaultSearch: { current: 1, pageSize: 20 },
    getFormInitialValues: (value) => ({
      keyword: value.keyword ?? (value.id === undefined ? undefined : String(value.id)),
      type: value.type,
      status: value.status
    }),
    getSearchFromForm: (value) => ({
      id: undefined,
      keyword: value.keyword?.trim() || undefined,
      type: value.type,
      status: value.status
    }),
    onQueryChange: () => setEditing(undefined),
    showTotal: (total) => `共 ${total} 份合同`
  })
  const openBatch = (mode: 'ids' | 'search', initialStatus?: ContractDocumentStatus) => {
    const count = mode === 'ids' ? selectedIds.length : (query.data?.total ?? 0)
    setStatusEditing({
      target: mode === 'ids' ? { mode, ids: [...selectedIds] } : { mode, search, expectedCount: count },
      label:
        mode === 'ids'
          ? `将修改已选的 ${count} 份合同。`
          : `将修改当前搜索条件下的全部 ${count} 份合同，包含其他分页。`,
      initialStatus
    })
  }
  const saveStatus = async (status: ContractDocumentStatus) => {
    if (!statusEditing) {
      return
    }
    try {
      const target = statusEditing.target
      const result = order
        ? await orderMutation.mutateAsync({
            orderType: order.type,
            target: { mode: 'ids', ids: [order.id] },
            status,
            documentIds: target !== 'allOrder' && target.mode === 'ids' ? target.ids : undefined
          })
        : await mutation.mutateAsync({ target: target as ContractDocumentBatchSetStatusInput['target'], status })
      void message.success(`已修改 ${result.updatedCount} 份合同，${result.unchangedCount} 份已处于目标状态`)
      setStatusEditing(undefined)
      setSelection({ search, ids: [] })
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '合同状态修改失败'))
    }
  }
  return (
    <>
      {query.isError && (
        <Alert
          className="mb-4"
          showIcon
          type="error"
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      <ProTable<ContractDocumentRow, Filters>
        cardProps={order ? false : undefined}
        rowKey="id"
        size={order ? 'small' : 'middle'}
        columns={contractDocumentColumns({
          compact: Boolean(order),
          canManage,
          canViewContent,
          canEditContent: canManageContent,
          canReadOrders,
          pending,
          getContentHref: (row) => {
            if (canManageContent && row.status === 'confirmed') {
              return router.buildLocation({
                to: '/contracts/edit/$documentId',
                params: { documentId: String(row.id) }
              }).href
            }
            if (order && canReadOrders) {
              if (order.type === 'rental') {
                return router.buildLocation({
                  to: '/orders/rentals/contract/$rentalId',
                  params: { rentalId: String(order.id) },
                  search: { documentId: row.id }
                }).href
              }
              return router.buildLocation({
                to: '/orders/service/contract/$orderId',
                params: { orderId: String(order.id) },
                search: { documentId: row.id }
              }).href
            }
            return router.buildLocation({
              to: '/contracts/view/$documentId',
              params: { documentId: String(row.id) }
            }).href
          },
          onStatus: (row, initialStatus) =>
            setStatusEditing({
              target: { mode: 'ids', ids: [row.id] },
              label: `合同：${row.title}（${row.contractNo}）`,
              defaultStatus: row.status,
              initialStatus
            }),
          onOrder: (source) => onOpenSourceOrder?.(source)
        })}
        columnsState={
          order ? { value: {} } : { persistenceKey: 'contract-documents:table', persistenceType: 'localStorage' }
        }
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        headerTitle={`合同文件（${query.data?.total ?? 0}）`}
        formRef={table.formRef}
        form={{
          initialValues: table.formInitialValues,
          name: order ? `order-contract-${order.type}-${order.id}` : 'contract-document-search'
        }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        pagination={{ ...table.pagination, total: query.data?.total ?? 0 }}
        options={{ reload: () => void query.refetch(), density: !order, fullScreen: !order, setting: !order }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: query.isError ? (
            '合同暂时无法加载'
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={order ? '当前订单暂无合同文件' : '暂无合同文件'} />
          )
        }}
        rowSelection={
          canManage
            ? {
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelection({ search, ids: keys.map(Number) }),
                getCheckboxProps: () => ({ disabled: pending || query.isFetching })
              }
            : false
        }
        toolBarRender={() =>
          canManage
            ? [
                <ContractDocumentToolbar
                  key="actions"
                  compact={Boolean(order)}
                  pending={pending || query.isFetching}
                  selectedCount={selectedIds.length}
                  total={query.data?.total ?? 0}
                  onBatch={openBatch}
                  onCreate={() => setEditing('new')}
                  onAll={(initialStatus) => {
                    if (!order) {
                      return
                    }
                    setStatusEditing({
                      target: 'allOrder',
                      label: `将修改订单 ${order.orderNo} 的全部合同文件，包含已作废合同及其他分页。`,
                      initialStatus
                    })
                  }}
                />
              ]
            : []
        }
      />
      {statusEditing && (
        <ContractStatusModal
          label={statusEditing.label}
          initialStatus={statusEditing.initialStatus}
          defaultStatus={statusEditing.defaultStatus}
          pending={pending}
          onSubmit={saveStatus}
          onClose={() => setStatusEditing(undefined)}
        />
      )}
      {editing && (
        <ContractDocumentEditorModal
          readOnly={!canManageContent}
          editing={editing}
          onClose={() => setEditing(undefined)}
          onSaved={async () => {
            await invalidate()
          }}
        />
      )}
    </>
  )
}
